// src/pages/ProfilePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_ENDPOINTS, API_BASE_URL, IMAGE_BASE_URL } from "../config/constants";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaArrowLeft,
  FaPencilAlt,
  FaSignOutAlt,
  FaCalendarAlt,
  FaTag,
  FaVolumeUp,
  FaVolumeMute,
  FaMobileAlt,
  FaMicrophone,
  FaMicrophoneSlash,
  FaTrashAlt,
  FaTimes,
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaQrcode,
  FaLink,
  FaRedo,
} from "react-icons/fa";
import { writeStoredSession } from "../utils/accountLinks";
import { resetLogRocketSession } from "../utils/logRocket";
import { APP_ESCAPE_EVENT, APP_SHORTCUT_EVENT, focusAndSelect } from "../utils/shortcuts";
import { resolveTaxRegistrationLabelFromProfile } from "../utils/taxDetails";
import { isMultiplePricingEnabled } from "../utils/priceLists";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { getOfflineProfile, saveOfflineProfile } from "../offline/profileStore";

const color = {
  brand: "var(--app-primary)",
  brandSoft: "var(--app-primary-soft)",
  text: "#111827",
  muted: "#6B7280",
  line: "#E5E7EB",
  bg: "#F2F5F9",
  danger: "#D32F2F",
  dangerSoft: "#FFF5F5",
  white: "#fff",
};

const VOICE_KEYS = {
  languageKey: "bill_voice_language", // "hi" | "en"
  enabledKey: "bill_voice_enabled", // "true" | "false"
};

const resolveProfileImageUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^(?:https?:|data:|blob:|file:)/i.test(url)) return url;
  return `${IMAGE_BASE_URL}${url}`;
};

const readProfileImage = (file) => new Promise((resolve, reject) => {
  if (!(file instanceof File)) return resolve("");
  if (file.size > 500 * 1024) {
    reject(new Error("Profile image must be smaller than 500 KB."));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ""));
  reader.onerror = () => reject(new Error("Unable to read the selected profile image."));
  reader.readAsDataURL(file);
});

const sessionWithUpdatedProfile = (session, profile) => {
  const restaurantId = String(profile?.id ?? session?.data?.id ?? "");
  const context = session?.account_link_context;
  const accounts = Array.isArray(context?.accounts)
    ? context.accounts.map((account) =>
        String(account?.id ?? "") === restaurantId
          ? { ...account, ...profile }
          : account)
    : null;
  return {
    ...(session || {}),
    success: true,
    data: profile,
    ...(accounts ? {
      account_link_context: { ...context, accounts },
    } : {}),
  };
};

const parseVersionValue = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const match = raw.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const getServerVersionCode = (userData = {}) => {
  const candidates = [
    "versioncode",
    "versionCode",
    "version_code",
    "appversioncode",
    "appVersionCode",
    "app_version_code",
    "required_version",
    "requiredVersionCode",
    "latestVersionCode",
    "requiredversioncode",
    "version",
  ];
  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(userData, key)) {
      const parsed = parseVersionValue(userData[key]);
      if (parsed !== null) return parsed;
    }
  }
  return null;
};

const fetchRequiredVersionFromCheckValid = async (restaurantId) => {
  if (!restaurantId) return null;
  try {
    const response = await fetch(
      API_ENDPOINTS.CHECK_VALID,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: restaurantId }),
      }
    );
    const result = await response.json().catch(() => ({}));
    if (!response.ok) return null;
    return parseVersionValue(result?.version);
  } catch (error) {
    return null;
  }
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditPage = location.pathname === "/profile/edit";

  // ----- view state -----
  const [user, setUser] = useState({});
  const isOwner = String(user?.role || user?.staffrole || "")
    .trim()
    .toLowerCase() === "owner";
  const taxRegistrationLabel = resolveTaxRegistrationLabelFromProfile(user);
  const [billVoiceEnabled, setBillVoiceEnabled] = useState(false);
  const [billVoiceLang, setBillVoiceLang] = useState("en");
  const [updateRequired, setUpdateRequired] = useState(false);
  const [serverVersionCode, setServerVersionCode] = useState(null);

  // delete modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // ----- edit form state (mirrors mobile EditProfile) -----
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [shopName, setShopName] = useState("");
  const [addr1, setAddr1] = useState("");
  const [addr2, setAddr2] = useState("");
  const [addr3, setAddr3] = useState("");
  const [cf1, setCf1] = useState("");
  const [cf2, setCf2] = useState("");
  const [cf3, setCf3] = useState("");
  const [gst, setGst] = useState("");
  const [upi, setUpi] = useState("");
  const [sound, setSound] = useState(1);
  const [vibration, setVibration] = useState(1);
  const [showLogo, setShowLogo] = useState(1);
  const [showQr, setShowQr] = useState(1);
  const [showPhone, setShowPhone] = useState(1);
  const [multiplePriceListsEnabled, setMultiplePriceListsEnabled] = useState(0);

  // avatar
  const [photoPreview, setPhotoPreview] = useState(null); // URL
  const [photoFile, setPhotoFile] = useState(null); // File
  const [photoChanged, setPhotoChanged] = useState(false);
  const editNameInputRef = useRef(null);
  const saveProfileRef = useRef(null);

  const localVersionCode = useMemo(() => {
    const fromEnv =
      process.env.REACT_APP_BUILD_VERSION ||
      process.env.REACT_APP_VERSION ||
      process.env.npm_package_version ||
      "0";
    return parseVersionValue(fromEnv) || 0;
  }, []);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!saved) {
      toast.error("No user data found");
      navigate("/login");
      return undefined;
    }
    let active = true;
    const fullOffline = isFullOfflineModeEnabled();

    const hydrateProfile = async () => {
      let effectiveUser = saved;
      if (fullOffline) {
        try {
          const offlineProfile = await getOfflineProfile(saved?.id);
          if (offlineProfile) {
            effectiveUser = { ...saved, ...offlineProfile, id: saved.id };
            const storedSession = JSON.parse(localStorage.getItem("userData") || "{}");
            writeStoredSession(
              sessionWithUpdatedProfile(storedSession, effectiveUser),
              { preserveAccountLinkContext: true, syncLogRocket: false }
            );
          }
        } catch (error) {
          toast.error(error?.message || "Unable to load offline profile.");
        }
      }
      if (!active) return;
      setUser(effectiveUser);
      primeEditForm(effectiveUser);

      const serverFromProfile = getServerVersionCode(effectiveUser);
      const required = fullOffline
        ? null
        : await fetchRequiredVersionFromCheckValid(effectiveUser?.id);
      if (!active) return;
      const chosen = required ?? serverFromProfile;
      setServerVersionCode(chosen);
      setUpdateRequired(
        chosen !== null && Number(chosen) > Number(localVersionCode || 0)
      );
    };
    hydrateProfile();

    // voice prefs mirror
    const lang = localStorage.getItem(VOICE_KEYS.languageKey);
    const enabledRaw = localStorage.getItem(VOICE_KEYS.enabledKey);
    const enabledParsed =
      enabledRaw === null || enabledRaw === undefined
        ? true
        : !["false", "0"].includes(String(enabledRaw).toLowerCase());
    setBillVoiceLang(lang === "hi" ? "hi" : "en");
    setBillVoiceEnabled(enabledParsed);

    return () => {
      active = false;
    };
  }, [navigate, localVersionCode]);

  const primeEditForm = (u) => {
    setName(u?.name || "");
    setEmail(u?.email || "");
    setShopName(u?.restaurant_name || "");
    setAddr1(u?.addressline1 || "");
    setAddr2(u?.addressline2 || "");
    setAddr3(u?.addressline3 || "");
    setCf1(u?.customfield1 || "");
    setCf2(u?.customfield2 || "");
    setCf3(u?.customfield3 || "");
    setGst(u?.gstno || "");
    setUpi(u?.upiaddress || "");
    setSound(u?.issoundenable ?? 1);
    setVibration(u?.isvibrationenable ?? 1);
    setShowLogo(
      typeof u?.showlogoonprinter === "number" ? u.showlogoonprinter : 1
    );
    setShowQr(
      typeof u?.showqrcodeonprinter === "number" ? u.showqrcodeonprinter : 1
    );
    setShowPhone(
      typeof u?.showphonenumberonprinter === "number"
        ? u.showphonenumberonprinter
        : 1
    );
    setMultiplePriceListsEnabled(isMultiplePricingEnabled(u) ? 1 : 0);

    const url = resolveProfileImageUrl(u?.avatar_url || u?.photo);
    setPhotoPreview(url || "/logo192.png");
    setPhotoFile(null);
    setPhotoChanged(false);
  };

  const yesNo = (v) => (String(v) === "1" || v === 1 ? "Yes" : "No");

  const avatarUrl = useMemo(() => {
    if (photoPreview) return photoPreview;
    const url = resolveProfileImageUrl(user?.avatar_url || user?.photo);
    return url || "/logo192.png";
  }, [user, photoPreview]);

  const planName = user?.package_type
    ? user.package_type.charAt(0).toUpperCase() + user.package_type.slice(1)
    : "No Plan";

  const validity = user?.validupto
    ? new Date(user.validupto).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "No Validity";

  const voiceLabel = billVoiceEnabled
    ? billVoiceLang === "hi"
      ? "Hindi"
      : "English"
    : "Off";
  const versionSubtitle = updateRequired
    ? serverVersionCode
      ? `Version ${serverVersionCode} is required`
      : "New build available"
    : "You are on the latest web build.";

  const openStoreLink = () => {
    window.open(
      "https://play.google.com/store/apps/details?id=com.billsarthi",
      "_blank"
    );
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      resetLogRocketSession();
      localStorage.removeItem("userData");
      toast.info("Logged out");
      navigate("/login");
    }
  };

  const openDelete = () => {
    setConfirmText("");
    setConfirmOpen(true);
  };
  const confirmMatches = confirmText.trim().toUpperCase() === "DELETE";

  const actuallyDeleteAccount = async () => {
    try {
      setDeleting(true);
      const id = user?.id;
      const res = await fetch(
        `${API_BASE_URL}/account/delete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: id }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      resetLogRocketSession();
      localStorage.removeItem("userData");
      toast.success("Account deleted");
      navigate("/login");
    } catch (e) {
      toast.error("Unable to delete account. Please try again.");
      console.error(e);
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  // ---------- edit modal helpers ----------
  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (isFullOfflineModeEnabled() && f.size > 500 * 1024) {
      toast.error("For offline storage, choose a profile image smaller than 500 KB.");
      e.target.value = "";
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    setPhotoChanged(true);
  };
  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoChanged(true); // treat as removal if backend supports flag
  };

  const saveProfile = async () => {
    const saved = JSON.parse(localStorage.getItem("userData") || "{}");
    const uid = saved?.data?.id;
    if (!uid) {
      toast.error("User not found in storage");
      return;
    }

    setSaving(true);
    try {
      const profileChanges = {
        restaurant_name: shopName,
        name,
        email,
        addressline1: addr1,
        addressline2: addr2,
        addressline3: addr3,
        issoundenable: sound,
        isvibrationenable: vibration,
        customfield1: cf1,
        customfield2: cf2,
        customfield3: cf3,
        gstno: gst,
        upiaddress: upi,
        showlogoonprinter: showLogo,
        showqrcodeonprinter: showQr,
        showphonenumberonprinter: showPhone,
        multiple_price_lists_enabled: multiplePriceListsEnabled,
      };

      if (isFullOfflineModeEnabled()) {
        if (photoChanged) {
          const localPhoto = photoFile instanceof File
            ? await readProfileImage(photoFile)
            : "";
          profileChanges.photo = localPhoto;
          profileChanges.avatar_url = localPhoto;
          profileChanges.restaurant_logo = localPhoto;
        }
        const result = await saveOfflineProfile(uid, profileChanges);
        const prev = JSON.parse(localStorage.getItem("userData") || "{}");
        const updatedUser = {
          ...(prev?.data || {}),
          ...(result?.profile || profileChanges),
          id: prev?.data?.id,
        };
        writeStoredSession(
          sessionWithUpdatedProfile(prev, updatedUser),
          { preserveAccountLinkContext: true, syncLogRocket: false }
        );
        localStorage.setItem(VOICE_KEYS.languageKey, billVoiceLang);
        localStorage.setItem(
          VOICE_KEYS.enabledKey,
          billVoiceEnabled ? "true" : "false"
        );
        setUser(updatedUser);
        primeEditForm(updatedUser);
        toast.success("Profile saved in Full Offline Mode");
        if (isEditPage) navigate("/profile");
        else setEditOpen(false);
        return;
      }

      const form = new FormData();
      form.append("restaurantid", String(uid));
      form.append("restaurant_name", shopName ?? "");
      form.append("name", name ?? "");
      form.append("email", email ?? "");
      form.append("addressline1", addr1 ?? "");
      form.append("addressline2", addr2 ?? "");
      form.append("addressline3", addr3 ?? "");
      form.append("issoundenable", String(sound ?? 0));
      form.append("isvibrationenable", String(vibration ?? 0));
      form.append("customfield1", cf1 ?? "");
      form.append("customfield2", cf2 ?? "");
      form.append("customfield3", cf3 ?? "");
      form.append("gstno", gst ?? "");
      form.append("upiaddress", upi ?? "");
      form.append("showlogoonprinter", String(showLogo ?? 0));
      form.append("showqrcodeonprinter", String(showQr ?? 0));
      form.append("showphonenumberonprinter", String(showPhone ?? 0));
      form.append("multiple_price_lists_enabled", String(multiplePriceListsEnabled ?? 0));

      // Only attach if changed & we have a new File
      if (photoChanged && photoFile instanceof File) {
        form.append("photo", photoFile, photoFile.name || "avatar.jpg");
      }
      // If you want to signal removal explicitly (only if your backend supports it):
      // if (photoChanged && !photoFile) form.append("removephoto", "1");

      const resp = await fetch(
        API_ENDPOINTS.EDIT_PROFILE,
        {
          method: "POST",
          body: form, // don't set Content-Type when using FormData
        }
      );

      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || result?.success === false) {
        throw new Error(result?.message || `HTTP ${resp.status}`);
      }

      // Merge returned data (if any), else our local fields
      const prev = JSON.parse(localStorage.getItem("userData") || "{}");
      const prevData = prev?.data || {};
      const updatedUser = result?.data
        ? { ...prevData, ...(result.data || {}) }
        : {
            ...prevData,
            restaurant_name: shopName,
            name,
            email,
            addressline1: addr1,
            addressline2: addr2,
            addressline3: addr3,
            issoundenable: sound,
            isvibrationenable: vibration,
            customfield1: cf1,
            customfield2: cf2,
            customfield3: cf3,
            gstno: gst,
            upiaddress: upi,
            showlogoonprinter: showLogo,
            showqrcodeonprinter: showQr,
            showphonenumberonprinter: showPhone,
            multiple_price_lists_enabled: multiplePriceListsEnabled,
          };

      writeStoredSession(
        sessionWithUpdatedProfile(prev, updatedUser),
        { preserveAccountLinkContext: true }
      );

      // persist voice prefs on web
      localStorage.setItem(VOICE_KEYS.languageKey, billVoiceLang);
      localStorage.setItem(
        VOICE_KEYS.enabledKey,
        billVoiceEnabled ? "true" : "false"
      );

      // reflect on screen
      setUser(updatedUser);
      toast.success("Profile updated");
      if (isEditPage) navigate("/profile");
      else setEditOpen(false);
    } catch (err) {
      console.error("Edit profile error:", err);
      toast.error(err.message || "Error updating profile");
    } finally {
      setSaving(false);
    }
  };
  saveProfileRef.current = saveProfile;

  useEffect(() => {
    if (!editOpen && !isEditPage) return undefined;
    const timeoutId = setTimeout(() => {
      focusAndSelect(editNameInputRef.current);
    }, 60);
    return () => clearTimeout(timeoutId);
  }, [editOpen, isEditPage]);

  useEffect(() => {
    const onShortcut = (event) => {
      switch (event?.detail?.id) {
        case "profile.edit":
          if (confirmOpen) return;
          event.preventDefault();
          navigate("/profile/edit");
          return;
        case "profile.save":
          if ((!editOpen && !isEditPage) || saving || confirmOpen) return;
          event.preventDefault();
          saveProfileRef.current?.();
          return;
        default:
          return;
      }
    };

    window.addEventListener(APP_SHORTCUT_EVENT, onShortcut);
    return () => window.removeEventListener(APP_SHORTCUT_EVENT, onShortcut);
  }, [confirmOpen, editOpen, isEditPage, navigate, saving]);

  useEffect(() => {
    const onEscape = (event) => {
      if (confirmOpen) {
        setConfirmOpen(false);
        event.preventDefault();
        return;
      }
      if (editOpen) {
        setEditOpen(false);
        event.preventDefault();
        return;
      }
      if (isEditPage) {
        navigate("/profile");
        event.preventDefault();
      }
    };

    window.addEventListener(APP_ESCAPE_EVENT, onEscape);
    return () => window.removeEventListener(APP_ESCAPE_EVENT, onEscape);
  }, [confirmOpen, editOpen, isEditPage, navigate]);

  return (
    <Wrap>
      <ToastContainer position="top-right" autoClose={2000} />
     
      {!isEditPage ? <Main>
        
        <HeroCard>
          <Avatar>
            <img src={avatarUrl} alt="avatar" />
          </Avatar>
          <Name>{user?.name || "—"}</Name>
          <Shop>{user?.restaurant_name || "—"}</Shop>

          <Badges>
            <Badge>
              <FaTag color={color.brand} />
              <span>
                Plan: <b>{planName}</b>
              </span>
            </Badge>
            <Badge>
              <FaCalendarAlt color={color.brand} />
              <span>
                Valid upto: <b>{validity}</b>
              </span>
            </Badge>
          </Badges>

        
        </HeroCard>

        <Section>
          <SectionTitle>Account Details</SectionTitle>

          <Grid>
            <DataCard label="Email" value={user?.email} />
            <DataCard label="Phone" value={user?.mobile_number} />
            <DataCard label="Address Line 1" value={user?.addressline1} />
            <DataCard label="Address Line 2" value={user?.addressline2} />
            <DataCard label="Address Line 3" value={user?.addressline3} />
            <DataCard label="Custom Field 1" value={user?.customfield1} />
            <DataCard label="Custom Field 2" value={user?.customfield2} />
            <DataCard label="Custom Field 3" value={user?.customfield3} />
            <DataCard label={taxRegistrationLabel} value={user?.gstno} />
            <DataCard label="UPI Address" value={user?.upiaddress} />
            <DataCard
              label="Logo on Bill"
              value={yesNo(user?.showlogoonprinter ?? 0)}
            />
            <DataCard
              label="Phone on Bill"
              value={yesNo(user?.showphonenumberonprinter ?? 1)}
            />
            <DataCard
              label="QRCode on Bill"
              value={yesNo(user?.showqrcodeonprinter ?? 0)}
            />
            <DataCard
              label="Multiple Price Lists"
              value={yesNo(user?.multiple_price_lists_enabled ?? 0)}
            />
          </Grid>

          <ScanBtn onClick={() => navigate("/menu-scanner")}>
            <FaQrcode />
            <span>Scan Menu with AI</span>
          </ScanBtn>

          <ScanBtn onClick={() => navigate("/linked-restaurants")}>
            <FaLink />
            <span>Linked Restaurants</span>
          </ScanBtn>

          <CtaRow>
            <OutlineBtn onClick={() => navigate("/profile/edit")}>
              <FaPencilAlt color={color.brand} />
              <span>Edit Profile</span>
            </OutlineBtn>

            <DangerOutlineBtn onClick={openDelete}>
              <FaTrashAlt color={color.danger} />
              <span>Delete Account</span>
            </DangerOutlineBtn>
          </CtaRow>

          {isOwner ? (
            <ResetAccountBtn onClick={() => navigate("/reset-account")}>
              <FaRedo />
              <span>Reset Account</span>
            </ResetAccountBtn>
          ) : null}
        </Section>
      </Main> : null}

      {/* --- Edit Modal (in-page) --- */}
      {(editOpen || isEditPage) && (
        <ModalOverlay $page={isEditPage}
          onMouseDown={(e) =>
            e.target === e.currentTarget && setEditOpen(false)
          }
        >
          <ModalCard $profile $page={isEditPage} onMouseDown={(e) => e.stopPropagation()}>
            <ModalHeader $profile>
              <div>
                <ModalEyebrow>Restaurant settings</ModalEyebrow>
                <ModalTitle>Edit Profile</ModalTitle>
                <ModalSubtitle>Update business details, bill preferences and POS features.</ModalSubtitle>
              </div>
              <CloseBtn onClick={() => isEditPage ? navigate("/profile") : setEditOpen(false)} aria-label="Close">
                <FaTimes />
              </CloseBtn>
            </ModalHeader>

            <ModalBody $profile>
              <EditSectionsGrid>
              <EditColumn>
              <ProfilePhotoPanel>
              <AvatarRow>
                <AvatarBig>
                  {photoPreview ? (
                    <img src={photoPreview} alt="avatar" />
                  ) : (
                    <div className="ph" />
                  )}
                </AvatarBig>
                <PhotoActions>
                  <PhotoTitle>Business logo</PhotoTitle>
                  <PhotoDescription>This logo can be used across your profile and printed bills.</PhotoDescription>
                  <PhotoButtonRow>
                  <FileInputLabel htmlFor="photoPick">
                    <FaUpload />
                    <span>Choose Photo</span>
                  </FileInputLabel>
                  <HiddenFile
                    id="photoPick"
                    accept="image/*"
                    type="file"
                    onChange={onPickFile}
                  />
                  {photoPreview && (
                    <RemoveBtn type="button" onClick={removePhoto}>
                      Remove
                    </RemoveBtn>
                  )}
                  </PhotoButtonRow>
                  <Hint>JPG/PNG, ideally square ~250×250.</Hint>
                </PhotoActions>
              </AvatarRow>
              </ProfilePhotoPanel>

              <FormSection>
                <FormSectionHeader><div><FormSectionTitle>Business details</FormSectionTitle><FormSectionSub>Basic owner and restaurant information.</FormSectionSub></div></FormSectionHeader>
                <FormGrid>
                <Field>
                  <Label>Name</Label>
                  <Input
                    ref={editNameInputRef}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                  />
                </Field>
                <Field>
                  <Label>Email</Label>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    type="email"
                    autoComplete="email"
                  />
                </Field>
                <Field col="2">
                  <Label>Shop Name</Label>
                  <Input
                    value={shopName}
                    onChange={(e) => setShopName(e.target.value)}
                    placeholder="Restaurant / Shop"
                  />
                </Field>
                </FormGrid>
              </FormSection>

              <FormSection>
                <FormSectionHeader><div><FormSectionTitle>Address</FormSectionTitle><FormSectionSub>Address shown in your business profile and bill header.</FormSectionSub></div></FormSectionHeader>
                <FormGrid>
                <Field col="2">
                  <Label>Address Line 1</Label>
                  <Input
                    value={addr1}
                    onChange={(e) => setAddr1(e.target.value)}
                    placeholder="Address line 1"
                  />
                </Field>
                <Field>
                  <Label>Address Line 2</Label>
                  <Input
                    value={addr2}
                    onChange={(e) => setAddr2(e.target.value)}
                    placeholder="Address line 2"
                  />
                </Field>
                <Field>
                  <Label>Address Line 3</Label>
                  <Input
                    value={addr3}
                    onChange={(e) => setAddr3(e.target.value)}
                    placeholder="Address line 3"
                  />
                </Field>
                </FormGrid>
              </FormSection>
              </EditColumn>

              <EditColumn>
              <FormSection>
                <FormSectionHeader><div><FormSectionTitle>Tax and custom details</FormSectionTitle><FormSectionSub>Registration, payment and optional bill information.</FormSectionSub></div></FormSectionHeader>
                <FormGrid>
                <Field>
                  <Label>Custom Field 1</Label>
                  <Input
                    value={cf1}
                    onChange={(e) => setCf1(e.target.value)}
                    placeholder="e.g. FSSAI no."
                  />
                </Field>
                <Field>
                  <Label>Custom Field 2</Label>
                  <Input
                    value={cf2}
                    onChange={(e) => setCf2(e.target.value)}
                    placeholder="Custom field 2"
                  />
                </Field>
                <Field>
                  <Label>Custom Field 3</Label>
                  <Input
                    value={cf3}
                    onChange={(e) => setCf3(e.target.value)}
                    placeholder="Custom field 3"
                  />
                </Field>

                <Field>
                  <Label>{taxRegistrationLabel}</Label>
                  <Input
                    value={gst}
                    onChange={(e) => setGst(e.target.value)}
                    placeholder={taxRegistrationLabel}
                  />
                </Field>
                <Field>
                  <Label>UPI Address</Label>
                  <Input
                    value={upi}
                    onChange={(e) => setUpi(e.target.value)}
                    placeholder="username@upi"
                  />
                </Field>
                </FormGrid>
              </FormSection>

              <FormSection>
                <FormSectionHeader><div><FormSectionTitle>POS and bill preferences</FormSectionTitle><FormSectionSub>Choose what staff use and customers see on bills.</FormSectionSub></div></FormSectionHeader>
                <FormGrid>
                <Field>
                  <Label>Sound</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setSound(1)} $on={sound === 1}>
                      On
                    </Toggle>
                    <Toggle onClick={() => setSound(0)} $on={sound === 0}>
                      Off
                    </Toggle>
                  </ToggleRow>
                </Field>
                <Field>
                  <Label>Vibration</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setVibration(1)} $on={vibration === 1}>
                      On
                    </Toggle>
                    <Toggle onClick={() => setVibration(0)} $on={vibration === 0}>
                      Off
                    </Toggle>
                  </ToggleRow>
                </Field>

                <Field>
                  <Label>Logo on Bill</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setShowLogo(1)} $on={showLogo === 1}>
                      Yes
                    </Toggle>
                    <Toggle onClick={() => setShowLogo(0)} $on={showLogo === 0}>
                      No
                    </Toggle>
                  </ToggleRow>
                </Field>
                <Field>
                  <Label>QR Code on Bill</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setShowQr(1)} $on={showQr === 1}>
                      Yes
                    </Toggle>
                    <Toggle onClick={() => setShowQr(0)} $on={showQr === 0}>
                      No
                    </Toggle>
                  </ToggleRow>
                </Field>
                <Field>
                  <Label>Phone on Bill</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setShowPhone(1)} $on={showPhone === 1}>
                      Yes
                    </Toggle>
                    <Toggle onClick={() => setShowPhone(0)} $on={showPhone === 0}>
                      No
                    </Toggle>
                  </ToggleRow>
                </Field>
                <Field>
                  <Label>Multiple Price Lists</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setMultiplePriceListsEnabled(1)} $on={multiplePriceListsEnabled === 1}>
                      Yes
                    </Toggle>
                    <Toggle onClick={() => setMultiplePriceListsEnabled(0)} $on={multiplePriceListsEnabled === 0}>
                      No
                    </Toggle>
                  </ToggleRow>
                  <Hint>Shows custom price lists on Web POS. Bills and reports keep using final item rates.</Hint>
                </Field>
                </FormGrid>
              </FormSection>
              </EditColumn>
              </EditSectionsGrid>
            </ModalBody>

            <ModalActions $profile>
              <ModalProgress>All changes are saved together.</ModalProgress>
              <ModalActionButtons>
                <CancelBtn onClick={() => navigate("/profile")} disabled={saving}>Cancel</CancelBtn>
                <PrimaryBtn onClick={saveProfile} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </PrimaryBtn>
              </ModalActionButtons>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* --- Delete Modal --- */}
      {confirmOpen && (
        <ModalOverlay
          onMouseDown={(e) =>
            e.target === e.currentTarget && setConfirmOpen(false)
          }
        >
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Confirm Account Deletion</ModalTitle>
              <CloseBtn
                onClick={() => setConfirmOpen(false)}
                aria-label="Close"
              >
                <FaTimes />
              </CloseBtn>
            </ModalHeader>

            <p style={{ color: "#6B7280", marginTop: 4 }}>
              This will permanently delete your account data from this device.
              This action cannot be undone.
            </p>

            <InfoBox>
              <InfoLine>
                <strong>Name:</strong> <span>{user?.name || "NA"}</span>
              </InfoLine>
              <InfoLine>
                <strong>Phone:</strong>{" "}
                <span>{user?.mobile_number || "NA"}</span>
              </InfoLine>
              <InfoLine>
                <strong>Email:</strong> <span>{user?.email || "NA"}</span>
              </InfoLine>
            </InfoBox>

            <ConfirmLabel>
              Type <b>DELETE</b> to confirm:
            </ConfirmLabel>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
            />

            <ModalActions>
              <CancelBtn
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
              >
                Cancel
              </CancelBtn>
              <DangerBtn
                onClick={actuallyDeleteAccount}
                disabled={!confirmMatches || deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </DangerBtn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </Wrap>
  );
};

export default ProfilePage;

/* ---------- small reusable ---------- */
const DataCard = ({ label, value }) => (
  <InfoCard>
    <InfoLabel>{label}</InfoLabel>
    <Value>{value ? value : "NA"}</Value>
  </InfoCard>
);

/* ---------- styled ---------- */
const Wrap = styled.div`
  background: ${color.bg};
  min-height: 100vh;
`;
const Header = styled.header`
  display: flex;
  align-items: center;
  background: ${color.brand};
  color: #fff;
  padding: 12px 16px;
`;
const IconBtn = styled.button`
  background: transparent;
  border: 0;
  color: inherit;
  font-size: 1.1rem;
  padding: 6px;
  cursor: pointer;
`;
const Title = styled.h1`
  margin: 0;
  flex: 1;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: 0.2px;
`;
const ActionRow = styled.div`
  display: flex;
  gap: 10px;
`;
const Main = styled.main`
  margin: 20px auto;
  padding: 0 16px;
`;
const StatusCard = styled.div`
  background: ${({ $danger }) => ($danger ? "#FFF5F5" : "#ECFDF5")};
  border: 1px solid ${({ $danger }) => ($danger ? "#FECACA" : "#BBF7D0")};
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;
const StatusLeft = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;
const StatusTitle = styled.div`
  font-weight: 800;
  color: #1f2937;
`;
const StatusSub = styled.div`
  color: #6b7280;
  font-size: 0.9rem;
`;
const PrimaryBtnSmall = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  background: ${color.brand};
  color: #fff;
  border: 0;
  font-weight: 800;
  cursor: pointer;
`;

const HeroCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  margin-bottom: 18px;
  box-shadow: 0 4px 16px rgba(2, 44, 77, 0.05);
`;
const Avatar = styled.div`
  width: 104px;
  height: 104px;
  border-radius: 50%;
  overflow: hidden;
  background: #ddd;
  margin: 0 auto 12px;
  border: 3px solid ${color.brand};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const Name = styled.h2`
  margin: 0;
  color: ${color.text};
  font-size: 1.4rem;
  font-weight: 900;
`;
const Shop = styled.p`
  margin: 4px 0 0;
  color: ${color.muted};
  font-weight: 600;
`;

const Badges = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
  justify-content: center;
`;
const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${color.line};
  background: #fafbff;
  padding: 6px 10px;
  border-radius: 999px;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
`;

const Chips = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;
const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? color.brand : "#EFEFF3")};
  border: 1px solid ${({ $on }) => ($on ? color.brand : "#E6E8EE")};
`;
const ChipText = styled.span`
  color: ${({ $on }) => ($on ? "#fff" : "#444")};
  font-size: 12px;
  font-weight: 700;
`;

const Section = styled.section`
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(2, 44, 77, 0.04);
`;
const SectionTitle = styled.h3`
  margin: 4px 2px 10px;
  font-size: 0.85rem;
  color: ${color.muted};
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
`;
const InfoCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid ${color.line};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;
const InfoLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${color.brand};
`;
const Value = styled.div`
  margin-top: 4px;
  color: ${color.text};
  font-size: 1rem;
`;

const CtaRow = styled.div`
  margin-top: 14px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
`;
const OutlineBtn = styled.button`
  flex: 1;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid ${color.brand};
  background: ${color.brandSoft};
  color: ${color.brand};
  font-weight: 800;
  cursor: pointer;
`;
const DangerOutlineBtn = styled(OutlineBtn)`
  border-color: ${color.danger};
  background: ${color.dangerSoft};
  color: ${color.danger};
`;
const ScanBtn = styled(OutlineBtn)`
  width: 100%;
  margin-top: 12px;
  justify-content: center;
  background: ${color.brand};
  color: #fff;
  border-color: ${color.brand};
  font-weight: 800;
  svg {
    color: #fff;
  }
`;
const ResetAccountBtn = styled(OutlineBtn)`
  width: 100%;
  margin-top: 12px;
  border-color: #d97706;
  background: #fffbeb;
  color: #b45309;
`;

/* ---- Modal base ---- */
const ModalOverlay = styled.div`
  position: ${({ $page }) => ($page ? "relative" : "fixed")};
  inset: ${({ $page }) => ($page ? "auto" : "0")};
  background: ${({ $page }) => ($page ? color.bg : "rgba(10, 26, 43, 0.62)")};
  backdrop-filter: ${({ $page }) => ($page ? "none" : "blur(5px)")};
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: ${({ $page }) => ($page ? "auto" : "1000")};
  min-height: ${({ $page }) => ($page ? "100%" : "auto")};
  @media (max-width: 600px) {
    padding: 8px;
    place-items: stretch;
  }
`;
const ModalCard = styled.div`
  width: 100%;
  max-width: ${({ $page, $profile }) => ($page ? "1440px" : $profile ? "1120px" : "820px")};
  background: #fff;
  border-radius: 20px;
  padding: ${({ $profile }) => ($profile ? "0" : "16px")};
  box-shadow: ${({ $page }) => ($page ? "0 8px 28px rgba(2,44,77,.08)" : "0 28px 90px rgba(2, 44, 77, 0.28)")};
  max-height: ${({ $page }) => ($page ? "none" : "94vh")};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @media (max-width: 600px) {
    max-height: calc(100vh - 16px);
    border-radius: 15px;
  }
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: ${({ $profile }) => ($profile ? "22px 26px" : "0")};
  background: ${({ $profile }) =>
    $profile
      ? "linear-gradient(135deg, #f4faff 0%, #ffffff 58%, #edf7ff 100%)"
      : "transparent"};
  border-bottom: ${({ $profile }) => ($profile ? `1px solid ${color.line}` : "0")};
  @media (max-width: 600px) {
    padding: ${({ $profile }) => ($profile ? "16px" : "0")};
  }
`;
const ModalEyebrow = styled.div`
  color: ${color.brand};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.7rem;
  font-weight: 900;
  margin-bottom: 4px;
`;
const ModalTitle = styled.h4`
  margin: 0;
  font-size: 1.45rem;
  color: #1f2937;
  font-weight: 900;
`;
const ModalSubtitle = styled.p`
  margin: 5px 0 0;
  color: ${color.muted};
  font-size: 0.88rem;
`;
const ModalTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 12px 26px;
  background: #fff;
  border-bottom: 1px solid ${color.line};
  @media (max-width: 700px) {
    padding: 10px 14px;
    gap: 5px;
  }
`;
const ModalTab = styled.button`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${({ $active }) => ($active ? color.brand : color.line)};
  border-radius: 11px;
  background: ${({ $active }) => ($active ? color.brand : "#f8fafc")};
  color: ${({ $active }) => ($active ? "#fff" : color.text)};
  font-size: 0.82rem;
  font-weight: 850;
  cursor: pointer;
  transition: 0.18s ease;
  span {
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
    border-radius: 7px;
    background: ${({ $active }) => ($active ? "rgba(255,255,255,.2)" : color.brandSoft)};
    color: ${({ $active }) => ($active ? "#fff" : color.brand)};
  }
  &:hover { border-color: ${color.brand}; }
  @media (max-width: 700px) {
    padding: 8px 5px;
    font-size: 0.68rem;
    gap: 4px;
    span { display: none; }
  }
`;
const CloseBtn = styled.button`
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  background: #fff;
  border: 1px solid ${color.line};
  border-radius: 11px;
  font-size: 1.1rem;
  cursor: pointer;
  color: #111;
  transition: 0.18s ease;
  &:hover {
    color: ${color.brand};
    border-color: ${color.brand};
    background: ${color.brandSoft};
  }
`;
const ModalBody = styled.div`
  overflow-y: ${({ $profile }) => ($profile ? "visible" : "auto")};
  padding: ${({ $profile }) => ($profile ? "22px 26px 8px" : "0 6px 0 0")};
  margin-top: ${({ $profile }) => ($profile ? "0" : "10px")};
  flex: 1;
  background: ${({ $profile }) => ($profile ? "#f6f8fb" : "transparent")};
  scrollbar-color: #b9d5e8 transparent;
  scrollbar-width: thin;
  @media (max-width: 600px) {
    padding: ${({ $profile }) => ($profile ? "14px" : "0 6px 0 0")};
    overflow-y: auto;
  }
`;

/* ---- Edit form ---- */
const AvatarRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin: 0;
  @media (max-width: 600px) {
    align-items: center;
    gap: 13px;
  }
`;
const AvatarBig = styled.div`
  width: 112px;
  height: 112px;
  min-width: 112px;
  border-radius: 20px;
  overflow: hidden;
  background: #eee;
  border: 3px solid #fff;
  box-shadow: 0 0 0 2px ${color.brand}, 0 10px 24px rgba(var(--app-primary-rgb), 0.16);
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .ph {
    width: 100%;
    height: 100%;
    background: #eee;
  }
`;
const ProfilePhotoPanel = styled.div`
  padding: 18px;
  margin-bottom: 16px;
  border: 1px solid #dceaf4;
  border-radius: 16px;
  background: linear-gradient(135deg, #fff 0%, #f0f8ff 100%);
`;
const EditSectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;
const EditColumn = styled.div`
  display: grid;
  gap: 14px;
  > * { margin-bottom: 0; }
`;
const PhotoActions = styled.div`
  min-width: 0;
`;
const PhotoTitle = styled.div`
  color: ${color.text};
  font-size: 1rem;
  font-weight: 900;
`;
const PhotoDescription = styled.div`
  color: ${color.muted};
  font-size: 0.82rem;
  margin: 3px 0 10px;
`;
const PhotoButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;
const HiddenFile = styled.input`
  display: none;
`;
const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${color.brand};
  color: #fff;
  cursor: pointer;
  font-weight: 700;
  transition: 0.18s ease;
  &:hover { background: #004a7e; }
`;
const RemoveBtn = styled.button`
  margin-left: 0;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${color.brand};
  background: #fff;
  color: ${color.brand};
  font-weight: 800;
  cursor: pointer;
`;
const Hint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $compact }) =>
    $compact ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))"};
  gap: 15px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const FormSection = styled.section`
  background: #fff;
  border: 1px solid ${color.line};
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 16px;
  box-shadow: 0 3px 12px rgba(2, 44, 77, 0.035);
`;
const FormSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding-bottom: 13px;
  margin-bottom: 15px;
  border-bottom: 1px solid #edf0f4;
`;
const FormSectionTitle = styled.h5`
  margin: 0;
  color: ${color.text};
  font-size: 0.98rem;
  font-weight: 900;
`;
const FormSectionSub = styled.p`
  margin: 2px 0 0;
  color: ${color.muted};
  font-size: 0.78rem;
`;
const Field = styled.div`
  grid-column: ${({ col }) => (col ? `span ${col}` : "span 1")};
  display: flex;
  flex-direction: column;
  @media (max-width: 720px) {
    grid-column: span 1;
  }
`;
const Label = styled.label`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${color.brand};
  margin-bottom: 6px;
`;
const Input = styled.input`
  border: 1px solid ${color.line};
  border-radius: 11px;
  padding: 12px 13px;
  font-size: 14px;
  background: #fbfcfd;
  color: ${color.text};
  outline: none;
  transition: 0.18s ease;
  &:hover { border-color: #b7c9d8; }
  &:focus {
    border-color: ${color.brand};
    background: #fff;
    box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.11);
  }
`;
const ToggleRow = styled.div`
  display: flex;
  gap: 8px;
`;
const Toggle = styled.button`
  flex: 1;
  padding: 10px 12px;
  border-radius: 11px;
  border: 1px solid ${({ $on }) => ($on ? color.brand : color.line)};
  background: ${({ $on }) => ($on ? color.brand : "#fff")};
  color: ${({ $on }) => ($on ? "#fff" : color.text)};
  font-weight: 800;
  cursor: pointer;
  transition: 0.18s ease;
  &:hover { border-color: ${color.brand}; }
`;
const Subtle = styled.div`
  font-size: 0.85rem;
  color: #546069;
  margin-bottom: 6px;
  margin-top: 6px;
`;
const LangRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 6px;
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
`;
const LangChip = styled.button`
  flex: 1;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${color.brand};
  background: ${({ $active }) => ($active ? color.brand : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : color.brand)};
  font-weight: 800;
  cursor: pointer;
`;

const ModalActions = styled.div`
  margin-top: ${({ $profile }) => ($profile ? "0" : "16px")};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: ${({ $profile }) => ($profile ? "16px 26px" : "0")};
  border-top: ${({ $profile }) => ($profile ? `1px solid ${color.line}` : "0")};
  background: #fff;
  @media (max-width: 600px) {
    padding: ${({ $profile }) => ($profile ? "12px 14px" : "0")};
    > button { min-width: 0; flex: 1; }
  }
`;
const ModalProgress = styled.div`
  color: ${color.muted};
  font-size: 0.8rem;
  font-weight: 800;
`;
const ModalActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 9px;
`;
const CancelBtn = styled.button`
  min-width: 110px;
  padding: 11px 18px;
  border-radius: 10px;
  border: 0;
  background: #eef2ff;
  color: #374151;
  font-weight: 800;
  cursor: pointer;
`;
const SecondaryBtn = styled(CancelBtn)`
  background: #fff;
  border: 1px solid ${color.line};
  color: ${color.brand};
`;
const PrimaryBtn = styled.button`
  min-width: 150px;
  padding: 11px 20px;
  border-radius: 10px;
  border: 0;
  background: ${color.brand};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  box-shadow: 0 7px 16px rgba(var(--app-primary-rgb), 0.2);
`;

/* ---- Delete modal specific ---- */
const InfoBox = styled.div`
  margin-top: 10px;
  background: #f7f8fc;
  border-radius: 12px;
  padding: 10px;
`;
const InfoLine = styled.div`
  color: #374151;
  margin-bottom: 4px;
  display: flex;
  gap: 6px;
  b,
  strong {
    color: #111827;
  }
  span {
    flex: 1;
  }
`;
const ConfirmLabel = styled.div`
  margin: 12px 0 6px;
  color: #374151;
  b {
    color: #e53935;
  }
`;
const DangerBtn = styled(PrimaryBtn)`
  background: ${color.danger};
`;
